import React, { useEffect, useState } from 'react';
import Cookies from 'universal-cookie';

import { getConfig, camelCaseObject } from '@edx/frontend-platform';

import { getAuthenticatedHttpClient, getAuthenticatedUser } from "@edx/frontend-platform/auth";
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';

function addPlugins(config, slot_name, plugins) {
  if (slot_name in config.pluginSlots === false) {
    config.pluginSlots[slot_name] = {
      keepDefault: true,
      plugins: []
    };
  }

  config.pluginSlots[slot_name].plugins.push(...plugins);
}

let themeCookie = 'indigo-toggle-dark';
let themeCookieExpiry = 90; // days

const AddDarkTheme = () => {
  const cookies = new Cookies();
  const isThemeToggleEnabled = getConfig().INDIGO_ENABLE_DARK_TOGGLE;

  const getCookieExpiry = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate() + themeCookieExpiry);
  };

  const getCookieOptions = () => {
    const serverURL = new URL(getConfig().LMS_BASE_URL);
    const options = { domain: serverURL.hostname, path: '/', expires: getCookieExpiry() };
    return options;
  };

  const addDarkThemeToIframes = () => {
    const iframes = document.getElementsByTagName('iframe');
    const iframesLength = iframes.length;
    if (iframesLength > 0) {
      Array.from({ length: iframesLength }).forEach((_, index) => {
        const style = document.createElement('style');
        style.textContent = `
          body{
            background-color: #0D0D0E;
            color: #ccc;
          }
          a {color: #ccc;}
          a:hover{color: #d3d3d3;}
          `;
        if (iframes[index].contentDocument) { iframes[index].contentDocument.head.appendChild(style); }
      });
    }
  };

  useEffect(() => {
    const theme = cookies.get(themeCookie);

    // - When page loads, Footer loads before MFE content. Since there is no iframe on page,
    // it does not append any class. MutationObserver observes changes in DOM and hence appends dark
    // attributes when iframe is added. After 15 sec, this observer is destroyed to conserve resources. 
    // - It has been added outside dark-theme condition so that it can be removed on Component Unmount.
    // - Observer can be passed to `addDarkThemeToIframes` function and disconnected after observing Iframe.
    // This approach has a limitation: the observer first detects the iframe and then detects the docSrc. 
    // We need to wait for docSrc to fully load before appending the style tag.
    const observer = new MutationObserver(() => {
      addDarkThemeToIframes();
    });

    if (isThemeToggleEnabled && theme === 'dark') {
      document.body.classList.add('indigo-dark-theme');
      
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer?.disconnect(), 15000); // clear after 15 sec to avoid resource usage

      cookies.set(themeCookie, theme, getCookieOptions());      //  on page load, update expiry
    }

    return () => observer?.disconnect();
  }, []);

  return (<div />);
};

// Language Widget Component
const LanguageWidget = ({ intl }) => {
  const [options, setOptions] = useState([]);
  const [showWidget, setShowWidget] = useState(false);


  // API functions
  const getLanguageOptions = async () => {
    const url = new URL(`${getConfig().LMS_BASE_URL}/wikimedia_general/api/v0/released_languages`);
    const { data } = await getAuthenticatedHttpClient().get(url.href, {});
    const options = data.released_languages.map((language) => ({ 
      value: language[0], 
      label: language[1] 
    }));
    return options;
  }

  const getShowLanguageWidget = async () => {
    const url = new URL(`${getConfig().LMS_BASE_URL}/wikimedia_general/api/v0/language_selector_is_enabled`);
    const { data } = await getAuthenticatedHttpClient().get(url.href);
    return camelCaseObject(data);
  }

  const postLanguageOptions = async (payload) => {
    const { username } = getAuthenticatedUser();
    const config = {
      headers: {
        'Content-Type': 'application/merge-patch+json',
      },
    };
    const url = new URL(`${getConfig().LMS_BASE_URL}/api/user/v1/preferences/${username}`);
    const { data } = await getAuthenticatedHttpClient().patch(url.href, payload, config);
    return camelCaseObject(data);
  }
  
  useEffect(() => {
    // Check if widget should be shown
    getShowLanguageWidget()
      .then((response) => {
        if (response.languageSelectorIsEnabled) {
          setShowWidget(true);
          // Fetch language options
          return getLanguageOptions();
        }
        return [];
      })
      .then((data) => {
        setOptions(data);
      })
      .catch((error) => {
        console.error('Unable to load language widget', error);
      });
  }, []);

  const handleChange = (e) => {
    e.preventDefault();
    const payload = {
      'pref-lang': e.target.value,
    };
    postLanguageOptions(payload)
      .then(() => {
        // Reload the page for the language to take effect
        window.location.reload();
      })
      .catch((error) => {
        console.error('Unable to patch user preference', error);
      });
  };

  // Don't render if widget shouldn't be shown or no options
  if (!showWidget || !options.length) {
    return null;
  }

  return (
    <select
      id="language-select"
      className="select-dropdown mx-2"
      onChange={handleChange}
      value={intl.locale}
      aria-label="Select language"
    >
      {options.map(({ value, label }) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </select>
  );
};

LanguageWidget.propTypes = {
  intl: intlShape.isRequired,
};

const LanguageWidgetIntl = injectIntl(LanguageWidget);


async function setConfig () {
  let config = {
    pluginSlots: {}
  };

  try {
    /* We can't assume FPF exists, as it's not declared as a dependency in all
     * MFEs, so we import it dynamically. In addition, for dynamic imports to
     * work with Webpack all of the code that actually uses the imported module
     * needs to be inside the `try{}` block.
     */
    const { DIRECT_PLUGIN, PLUGIN_OPERATIONS } = await import('@openedx/frontend-plugin-framework');
    if (process.env.APP_ID == 'authoring') {
    }
    if (process.env.APP_ID == 'account') {
const { default: IndigoFooter } = await import('@edly-io/wikimedia-frontend-component-footer');
const { AppContext } = await import ('@edx/frontend-platform/react');
const { useContext } = await import ('react');
const { NotificationsTray } = await import('@edx/frontend-plugin-notifications');
      addPlugins(config, 'footer_slot', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'default_contents',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: <IndigoFooter />,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'desktop_main_menu_slot', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
              op: PLUGIN_OPERATIONS.Insert,
              widget: {
                id: 'dynamic_tabs',
                type: DIRECT_PLUGIN,
                RenderWidget: () => {
                  const [tabs, setTabs] = React.useState([]);
                  const url = `${getConfig().LMS_BASE_URL}/wikimedia_general/api/v0/lms_tabs`;

                  React.useEffect(() => {
                    getAuthenticatedHttpClient().get(url)
                      .then(response => {
                        if (response?.data?.tabs) {
                          setTabs(response.data.tabs);
                        }
                      })
                      .catch(error => {
                        console.error("Failed to load LMS tabs:", error);
                      });
                  }, []);

                  return (
                    <>
                      {tabs.map(tab => (
                        <a
                          key={tab.id}
                          className="nav-link"
                          href={tab.url}
                        >
                          {tab.name}
                        </a>
                      ))}
                    </>
                  );
                },
              },
            }
            ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'language_selector_widget',
                    type: DIRECT_PLUGIN,
                    priority: 9,
                    RenderWidget: LanguageWidgetIntl,
                },
            },
            {
              op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'notification-drawer-widget',
                    priority: 10,
                    type: DIRECT_PLUGIN,
                    RenderWidget: NotificationsTray,
                },
            },
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_learning_help.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'language_selector_widget',
                    type: DIRECT_PLUGIN,
                    priority: 9,
                    RenderWidget: LanguageWidgetIntl,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'notification-drawer-widget',
                    priority: 10,
                    type: DIRECT_PLUGIN,
                    RenderWidget: NotificationsTray,
                },
            },
            ]);
    }
    if (process.env.APP_ID == 'communications') {
const { default: IndigoFooter } = await import('@edly-io/wikimedia-frontend-component-footer');
const { AppContext } = await import ('@edx/frontend-platform/react');
const { useContext } = await import ('react');
const { NotificationsTray } = await import('@edx/frontend-plugin-notifications');
      addPlugins(config, 'footer_slot', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'default_contents',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: <IndigoFooter />,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'desktop_main_menu_slot', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
              op: PLUGIN_OPERATIONS.Insert,
              widget: {
                id: 'dynamic_tabs',
                type: DIRECT_PLUGIN,
                RenderWidget: () => {
                  const [tabs, setTabs] = React.useState([]);
                  const url = `${getConfig().LMS_BASE_URL}/wikimedia_general/api/v0/lms_tabs`;

                  React.useEffect(() => {
                    getAuthenticatedHttpClient().get(url)
                      .then(response => {
                        if (response?.data?.tabs) {
                          setTabs(response.data.tabs);
                        }
                      })
                      .catch(error => {
                        console.error("Failed to load LMS tabs:", error);
                      });
                  }, []);

                  return (
                    <>
                      {tabs.map(tab => (
                        <a
                          key={tab.id}
                          className="nav-link"
                          href={tab.url}
                        >
                          {tab.name}
                        </a>
                      ))}
                    </>
                  );
                },
              },
            }
            ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'language_selector_widget',
                    type: DIRECT_PLUGIN,
                    priority: 9,
                    RenderWidget: LanguageWidgetIntl,
                },
            },
            {
              op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'notification-drawer-widget',
                    priority: 10,
                    type: DIRECT_PLUGIN,
                    RenderWidget: NotificationsTray,
                },
            },
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_learning_help.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'language_selector_widget',
                    type: DIRECT_PLUGIN,
                    priority: 9,
                    RenderWidget: LanguageWidgetIntl,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'notification-drawer-widget',
                    priority: 10,
                    type: DIRECT_PLUGIN,
                    RenderWidget: NotificationsTray,
                },
            },
            ]);
    }
    if (process.env.APP_ID == 'discussions') {
const { default: IndigoFooter } = await import('@edly-io/wikimedia-frontend-component-footer');
const { AppContext } = await import ('@edx/frontend-platform/react');
const { useContext } = await import ('react');
const { NotificationsTray } = await import('@edx/frontend-plugin-notifications');

const { UsernameMention } = await import('frontend-plugins-wikilearn');
      addPlugins(config, 'footer_slot', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'default_contents',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: <IndigoFooter />,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'desktop_main_menu_slot', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
              op: PLUGIN_OPERATIONS.Insert,
              widget: {
                id: 'dynamic_tabs',
                type: DIRECT_PLUGIN,
                RenderWidget: () => {
                  const [tabs, setTabs] = React.useState([]);
                  const url = `${getConfig().LMS_BASE_URL}/wikimedia_general/api/v0/lms_tabs`;

                  React.useEffect(() => {
                    getAuthenticatedHttpClient().get(url)
                      .then(response => {
                        if (response?.data?.tabs) {
                          setTabs(response.data.tabs);
                        }
                      })
                      .catch(error => {
                        console.error("Failed to load LMS tabs:", error);
                      });
                  }, []);

                  return (
                    <>
                      {tabs.map(tab => (
                        <a
                          key={tab.id}
                          className="nav-link"
                          href={tab.url}
                        >
                          {tab.name}
                        </a>
                      ))}
                    </>
                  );
                },
              },
            }
            ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'language_selector_widget',
                    type: DIRECT_PLUGIN,
                    priority: 9,
                    RenderWidget: LanguageWidgetIntl,
                },
            },
            {
              op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'notification-drawer-widget',
                    priority: 10,
                    type: DIRECT_PLUGIN,
                    RenderWidget: NotificationsTray,
                },
            },
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_learning_help.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'language_selector_widget',
                    type: DIRECT_PLUGIN,
                    priority: 9,
                    RenderWidget: LanguageWidgetIntl,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'notification-drawer-widget',
                    priority: 10,
                    type: DIRECT_PLUGIN,
                    RenderWidget: NotificationsTray,
                },
            },
            ]);
      addPlugins(config, 'user_mention_plugin', [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'user_mention_plugin',
            type: DIRECT_PLUGIN,
            priority: 60,
            RenderWidget: UsernameMention,
          },
        }]);
    }
    if (process.env.APP_ID == 'gradebook') {
    }
    if (process.env.APP_ID == 'learner-dashboard') {
const { default: IndigoFooter } = await import('@edly-io/wikimedia-frontend-component-footer');
const { AppContext } = await import ('@edx/frontend-platform/react');
const { useContext } = await import ('react');
const { NotificationsTray } = await import('@edx/frontend-plugin-notifications');
      addPlugins(config, 'footer_slot', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'default_contents',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: <IndigoFooter />,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'desktop_main_menu_slot', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
              op: PLUGIN_OPERATIONS.Insert,
              widget: {
                id: 'dynamic_tabs',
                type: DIRECT_PLUGIN,
                RenderWidget: () => {
                  const [tabs, setTabs] = React.useState([]);
                  const url = `${getConfig().LMS_BASE_URL}/wikimedia_general/api/v0/lms_tabs`;

                  React.useEffect(() => {
                    getAuthenticatedHttpClient().get(url)
                      .then(response => {
                        if (response?.data?.tabs) {
                          setTabs(response.data.tabs);
                        }
                      })
                      .catch(error => {
                        console.error("Failed to load LMS tabs:", error);
                      });
                  }, []);

                  return (
                    <>
                      {tabs.map(tab => (
                        <a
                          key={tab.id}
                          className="nav-link"
                          href={tab.url}
                        >
                          {tab.name}
                        </a>
                      ))}
                    </>
                  );
                },
              },
            }
            ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'language_selector_widget',
                    type: DIRECT_PLUGIN,
                    priority: 9,
                    RenderWidget: LanguageWidgetIntl,
                },
            },
            {
              op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'notification-drawer-widget',
                    priority: 10,
                    type: DIRECT_PLUGIN,
                    RenderWidget: NotificationsTray,
                },
            },
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_learning_help.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'language_selector_widget',
                    type: DIRECT_PLUGIN,
                    priority: 9,
                    RenderWidget: LanguageWidgetIntl,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'notification-drawer-widget',
                    priority: 10,
                    type: DIRECT_PLUGIN,
                    RenderWidget: NotificationsTray,
                },
            },
            ]);
    }
    if (process.env.APP_ID == 'learning') {
const { default: IndigoFooter } = await import('@edly-io/wikimedia-frontend-component-footer');
const { AppContext } = await import ('@edx/frontend-platform/react');
const { useContext } = await import ('react');
const { NotificationsTray } = await import('@edx/frontend-plugin-notifications');
      addPlugins(config, 'footer_slot', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'default_contents',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: <IndigoFooter />,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'desktop_main_menu_slot', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
              op: PLUGIN_OPERATIONS.Insert,
              widget: {
                id: 'dynamic_tabs',
                type: DIRECT_PLUGIN,
                RenderWidget: () => {
                  const [tabs, setTabs] = React.useState([]);
                  const url = `${getConfig().LMS_BASE_URL}/wikimedia_general/api/v0/lms_tabs`;

                  React.useEffect(() => {
                    getAuthenticatedHttpClient().get(url)
                      .then(response => {
                        if (response?.data?.tabs) {
                          setTabs(response.data.tabs);
                        }
                      })
                      .catch(error => {
                        console.error("Failed to load LMS tabs:", error);
                      });
                  }, []);

                  return (
                    <>
                      {tabs.map(tab => (
                        <a
                          key={tab.id}
                          className="nav-link"
                          href={tab.url}
                        >
                          {tab.name}
                        </a>
                      ))}
                    </>
                  );
                },
              },
            }
            ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'language_selector_widget',
                    type: DIRECT_PLUGIN,
                    priority: 9,
                    RenderWidget: LanguageWidgetIntl,
                },
            },
            {
              op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'notification-drawer-widget',
                    priority: 10,
                    type: DIRECT_PLUGIN,
                    RenderWidget: NotificationsTray,
                },
            },
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_learning_help.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'language_selector_widget',
                    type: DIRECT_PLUGIN,
                    priority: 9,
                    RenderWidget: LanguageWidgetIntl,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'notification-drawer-widget',
                    priority: 10,
                    type: DIRECT_PLUGIN,
                    RenderWidget: NotificationsTray,
                },
            },
            ]);
    }
    if (process.env.APP_ID == 'ora-grading') {
    }
    if (process.env.APP_ID == 'profile') {
const { default: IndigoFooter } = await import('@edly-io/wikimedia-frontend-component-footer');
const { AppContext } = await import ('@edx/frontend-platform/react');
const { useContext } = await import ('react');
const { NotificationsTray } = await import('@edx/frontend-plugin-notifications');
      addPlugins(config, 'footer_slot', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'default_contents',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: <IndigoFooter />,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'desktop_main_menu_slot', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
              op: PLUGIN_OPERATIONS.Insert,
              widget: {
                id: 'dynamic_tabs',
                type: DIRECT_PLUGIN,
                RenderWidget: () => {
                  const [tabs, setTabs] = React.useState([]);
                  const url = `${getConfig().LMS_BASE_URL}/wikimedia_general/api/v0/lms_tabs`;

                  React.useEffect(() => {
                    getAuthenticatedHttpClient().get(url)
                      .then(response => {
                        if (response?.data?.tabs) {
                          setTabs(response.data.tabs);
                        }
                      })
                      .catch(error => {
                        console.error("Failed to load LMS tabs:", error);
                      });
                  }, []);

                  return (
                    <>
                      {tabs.map(tab => (
                        <a
                          key={tab.id}
                          className="nav-link"
                          href={tab.url}
                        >
                          {tab.name}
                        </a>
                      ))}
                    </>
                  );
                },
              },
            }
            ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'language_selector_widget',
                    type: DIRECT_PLUGIN,
                    priority: 9,
                    RenderWidget: LanguageWidgetIntl,
                },
            },
            {
              op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'notification-drawer-widget',
                    priority: 10,
                    type: DIRECT_PLUGIN,
                    RenderWidget: NotificationsTray,
                },
            },
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_learning_help.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'language_selector_widget',
                    type: DIRECT_PLUGIN,
                    priority: 9,
                    RenderWidget: LanguageWidgetIntl,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'notification-drawer-widget',
                    priority: 10,
                    type: DIRECT_PLUGIN,
                    RenderWidget: NotificationsTray,
                },
            },
            ]);
    }
    if (process.env.APP_ID == 'learner-record') {
const { default: IndigoFooter } = await import('@edly-io/wikimedia-frontend-component-footer');
const { AppContext } = await import ('@edx/frontend-platform/react');
const { useContext } = await import ('react');
const { NotificationsTray } = await import('@edx/frontend-plugin-notifications');
      addPlugins(config, 'footer_slot', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'default_contents',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: <IndigoFooter />,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'desktop_main_menu_slot', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
              op: PLUGIN_OPERATIONS.Insert,
              widget: {
                id: 'dynamic_tabs',
                type: DIRECT_PLUGIN,
                RenderWidget: () => {
                  const [tabs, setTabs] = React.useState([]);
                  const url = `${getConfig().LMS_BASE_URL}/wikimedia_general/api/v0/lms_tabs`;

                  React.useEffect(() => {
                    getAuthenticatedHttpClient().get(url)
                      .then(response => {
                        if (response?.data?.tabs) {
                          setTabs(response.data.tabs);
                        }
                      })
                      .catch(error => {
                        console.error("Failed to load LMS tabs:", error);
                      });
                  }, []);

                  return (
                    <>
                      {tabs.map(tab => (
                        <a
                          key={tab.id}
                          className="nav-link"
                          href={tab.url}
                        >
                          {tab.name}
                        </a>
                      ))}
                    </>
                  );
                },
              },
            }
            ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'language_selector_widget',
                    type: DIRECT_PLUGIN,
                    priority: 9,
                    RenderWidget: LanguageWidgetIntl,
                },
            },
            {
              op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'notification-drawer-widget',
                    priority: 10,
                    type: DIRECT_PLUGIN,
                    RenderWidget: NotificationsTray,
                },
            },
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_learning_help.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'language_selector_widget',
                    type: DIRECT_PLUGIN,
                    priority: 9,
                    RenderWidget: LanguageWidgetIntl,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'notification-drawer-widget',
                    priority: 10,
                    type: DIRECT_PLUGIN,
                    RenderWidget: NotificationsTray,
                },
            },
            ]);
    }
    if (process.env.APP_ID == 'messenger') {
const { default: IndigoFooter } = await import('@edly-io/wikimedia-frontend-component-footer');
const { AppContext } = await import ('@edx/frontend-platform/react');
const { useContext } = await import ('react');
const { NotificationsTray } = await import('@edx/frontend-plugin-notifications');
      addPlugins(config, 'footer_slot', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'default_contents',
                    type: DIRECT_PLUGIN,
                    priority: 1,
                    RenderWidget: <IndigoFooter />,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'read_theme_cookie',
                    type: DIRECT_PLUGIN,
                    priority: 2,
                    RenderWidget: AddDarkTheme,
                },
            },
  ]);
      addPlugins(config, 'desktop_main_menu_slot', [
            {
                op: PLUGIN_OPERATIONS.Hide,
                widgetId: 'default_contents',
            },
            {
              op: PLUGIN_OPERATIONS.Insert,
              widget: {
                id: 'dynamic_tabs',
                type: DIRECT_PLUGIN,
                RenderWidget: () => {
                  const [tabs, setTabs] = React.useState([]);
                  const url = `${getConfig().LMS_BASE_URL}/wikimedia_general/api/v0/lms_tabs`;

                  React.useEffect(() => {
                    getAuthenticatedHttpClient().get(url)
                      .then(response => {
                        if (response?.data?.tabs) {
                          setTabs(response.data.tabs);
                        }
                      })
                      .catch(error => {
                        console.error("Failed to load LMS tabs:", error);
                      });
                  }, []);

                  return (
                    <>
                      {tabs.map(tab => (
                        <a
                          key={tab.id}
                          className="nav-link"
                          href={tab.url}
                        >
                          {tab.name}
                        </a>
                      ))}
                    </>
                  );
                },
              },
            }
            ]);
      addPlugins(config, 'desktop_secondary_menu_slot', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'language_selector_widget',
                    type: DIRECT_PLUGIN,
                    priority: 9,
                    RenderWidget: LanguageWidgetIntl,
                },
            },
            {
              op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'notification-drawer-widget',
                    priority: 10,
                    type: DIRECT_PLUGIN,
                    RenderWidget: NotificationsTray,
                },
            },
            ]);
      addPlugins(config, 'org.openedx.frontend.layout.header_learning_help.v1', [
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'language_selector_widget',
                    type: DIRECT_PLUGIN,
                    priority: 9,
                    RenderWidget: LanguageWidgetIntl,
                },
            },
            {
                op: PLUGIN_OPERATIONS.Insert,
                widget: {
                    id: 'notification-drawer-widget',
                    priority: 10,
                    type: DIRECT_PLUGIN,
                    RenderWidget: NotificationsTray,
                },
            },
            ]);
    }
  } catch (err) { console.error("env.config.jsx failed to apply: ", err);}

  return config;
}

export default setConfig;